<template>
  <div class="p-3 grid grid-cols-10 gap-2">
    <div class="flex flex-col gap-2 col-span-10 lg:col-span-4">
      <Card class="w-full" pt:title:class="text-center text-2xl font-bold">
        <template #title>Routing Information</template>
        <template #content>
          <div class="grid grid-cols-3 gap-2 px-5 my-auto h-full justify-center" pt:body:class="h-full" pt:content:class="h-full">
            <div class="col-span-3 flex flex-col gap-1 mb-3">
              <p>Bank Name</p>
              <span class="font-bold text-3xl">{{ bankName }}</span>
            </div>
            <div class="col-span-1 flex flex-col gap-1">
              <p>ABA Routing Number</p>
              <span class="font-bold text-lg">{{ abaNumber }}</span>
            </div>
            <div class="col-span-1 flex flex-col gap-1">
              <p>Account Number</p>
              <span class="font-bold text-lg">{{ accountNumber }}</span>
            </div>
            <div class="col-span-1 flex flex-col gap-1">
              <p>Bank Number</p>
              <span class="font-bold text-lg">{{ ledger.checkingAccount.bankNumber }}</span>
            </div>
          </div>
        </template>
      </Card>
      <Card class="w-full" pt:title:class="text-center text-2xl font-bold">
        <template #title>Ledger Details</template>
        <template #content>
          <div class="grid grid-cols-2 gap-2 px-5 my-auto h-full justify-items-center" pt:body:class="h-full" pt:content:class="h-full">
            <div class="flex flex-col gap-1">
              <p>Next Check #</p>
              <span class="font-bold text-3xl mb-3">{{ ledger.checkingAccount.nextCheckNumber }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <p>Escrow #</p>
              <span class="font-bold text-3xl mb-3">{{ ledger.checkingAccount.escrowNumber }}</span>
            </div>
            <div class="flex flex-col">
              <Knob v-model="exportedCount" valueTemplate="{value}%" readonly :size="140" />
              <p class="text-center font-bold -mt-3">Payments Exported</p>
            </div>
            <div class="flex flex-col">
              <Knob v-model="clearedCount" valueTemplate="{value}%" readonly :size="140" />
              <p class="text-center font-bold -mt-3">Payments Cleared</p>
            </div>
          </div>
        </template>
      </Card>
    </div>
    <Card class="col-span-10 lg:col-span-3 flex-none h-auto" pt:body:class="h-full" pt:content:class="h-full">
      <template #title>Payment Statuses</template>
      <template #content>
        <PieChart ref="statusChart" :info="statusInfo"></PieChart>
      </template>
    </Card>
    <Card class="col-span-10 lg:col-span-3 flex-none h-auto" pt:body:class="h-full" pt:content:class="h-full">
      <template #content>
        <BarChart ref="transactionChart" :info="transactionInfo"></BarChart>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { BarChartData, BarChartInfo, PieChartInfo } from "@/classes/ChartInfo";
import type { LedgerDashboard } from "@/types/Ledger";

const ledger = defineModel<LedgerDashboard>("ledger", { required: true });
const statusChart = ref();
const transactionChart = ref();

const dataLoaded = (): void => {
  statusChart.value.loadingComplete();
  transactionChart.value.loadingComplete();
};

defineExpose({ dataLoaded });

const getStatusData = (): number[] => {
  let unprocessedCount = 0;
  let paidCount = 0;
  let voidCount = 0;

  for (let payment of ledger.value.checkLedgerList) {
    if (payment.voided) {
      voidCount++;
    } else if (payment.paid) {
      paidCount++;
    } else {
      unprocessedCount++;
    }
  }
  return [unprocessedCount, paidCount, voidCount];
};

const statusInfo = ref<PieChartInfo>(new PieChartInfo());
statusInfo.value.labels = ["Unprocessed", "Paid", "Voided"];
statusInfo.value.data = getStatusData();
statusInfo.value.height = "h-[470px]";
statusInfo.value.donut = true;
statusInfo.value.title = "Total";
statusInfo.value.titleTopPosition = "35%";

const getTransactionData = (): BarChartData[] => {
  const transactionCount = new Map<string, number>();

  for (let payment of ledger.value.checkLedgerList) {
    if (transactionCount.has(payment.checkAction)) {
      let currentCount = transactionCount.get(payment.checkAction);
      transactionCount.set(payment.checkAction, currentCount! + 1);
    } else {
      transactionCount.set(payment.checkAction, 1);
    }
  }

  transactionInfo.value.labels = Array.from(transactionCount.keys());
  const data: BarChartData = new BarChartData();
  data.name = "Transaction Type";

  for (let [key, value] of transactionCount) {
    data.data.push(value);
  }

  return [data];
};

const transactionInfo = ref<BarChartInfo>(new BarChartInfo());
transactionInfo.value.title = "Transaction Types";
transactionInfo.value.minInterval = 1;
transactionInfo.value.bars = getTransactionData();
transactionInfo.value.height = "h-[500px]";
transactionInfo.value.xaxisRotate = -50;
transactionInfo.value.showLegend = false;

const clearedCount = computed<number>(() =>
  Math.trunc((ledger.value.checkLedgerList.filter((item) => item.cleared).length / ledger.value.checkLedgerList.length) * 100)
);
const exportedCount = computed<number>(() =>
  Math.trunc((ledger.value.checkLedgerList.filter((item) => item.exported).length / ledger.value.checkLedgerList.length) * 100)
);

const bankName = computed<string>(() => (ledger.value.checkingAccount.bankName ? ledger.value.checkingAccount.bankName : "NONE"));
const abaNumber = computed<string>(() => (ledger.value.checkingAccount.bankABANumber ? ledger.value.checkingAccount.bankABANumber : "NONE"));
const accountNumber = computed<string>(() => (ledger.value.checkingAccount.accountNumber ? ledger.value.checkingAccount.accountNumber : "NONE"));
</script>
